import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';

type SiteCTAProps = {
  ctaHref?: string;
  ctaLabel?: string;
};

const DEFAULT_CTA_HREF = 'https://cal.com/noah.mccloud/optigolddiscoverycall';
const DEFAULT_CTA_LABEL = 'Schedule a Discovery Call';

const SiteCTA: React.FC<SiteCTAProps> = ({
  ctaHref = DEFAULT_CTA_HREF,
  ctaLabel = DEFAULT_CTA_LABEL,
}) => {
  return (
    <section className="py-20 bg-gradient-to-b from-gray-900 via-black to-black relative overflow-hidden">
      {/* Grid pattern overlay that only appears at the very bottom */}
      <div 
        className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.05) 100%),
                           linear-gradient(to right, #ffffff05 1px, transparent 1px),
                           linear-gradient(to bottom, #ffffff05 1px, transparent 1px)`,
          backgroundSize: '100% 100%, 24px 24px, 24px 24px',
          backgroundPosition: '0 0, 0 0, 0 0'
        }}
      ></div>
      
      {/* Decorative floating orb - only on the right */}
      <motion.div 
        className="absolute top-0 right-0 w-96 h-96 bg-gold/10 rounded-full blur-3xl -mr-20 -mt-20"
        animate={{
          x: [0, 30, 0],
          y: [0, -20, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <div className="container mx-auto px-4 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Small accent badge */}
          <div className="inline-flex items-center gap-2 mb-6">
            <Sparkles className="w-4 h-4 text-gold" />
            <span className="text-gold text-sm font-bold uppercase tracking-wider">Let's Connect</span>
            <Sparkles className="w-4 h-4 text-gold" />
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold">
            Ready to Unlock the Full Potential of Your PMO?
          </h2>
          <p className="text-gray-300 text-xl mb-10 max-w-3xl mx-auto leading-relaxed">
            Whether building from the ground up or refining what's in place, OptiGold is your trusted partner in achieving meaningful impact.
          </p>
          
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block"
          >
            <a
              href={ctaHref}
              className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-gold to-amber-400 hover:from-amber-400 hover:to-gold text-black font-bold text-lg rounded-full shadow-2xl hover:shadow-gold/50 transition-all duration-300 group"
            >
              {ctaLabel}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Soft fade to pure black at bottom for seamless footer transition */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-black pointer-events-none"></div>
    </section>
  );
};

export default SiteCTA;
